/* Stompies hero: scroll-scrubbed stomp video plus the phone buy bar.
   The video is fetched whole (all-keyframe encode) once the hero photo has loaded, then its frame follows scroll.
   Reduced motion or data saver keeps the still photo.
   Horizon scrolls inside .page-wrapper rather than the window, so scroll position is read from
   whichever of the two is actually scrolling. */
(() => {
  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));

  /* Nearest ancestor that scrolls its own content (Horizon: .page-wrapper), or null for the window */
  function findScroller(el) {
    for (let p = el.parentElement; p && p !== document.body && p !== document.documentElement; p = p.parentElement) {
      const oy = getComputedStyle(p).overflowY;
      if (oy === 'auto' || oy === 'scroll' || oy === 'overlay') return p;
    }
    return null;
  }

  document.querySelectorAll('[data-stp-hero]').forEach((hero) => {
    const stage = hero.querySelector('.stp-hero__stage');
    const media = hero.querySelector('.stp-hero__media');
    const video = hero.querySelector('.stp-hero__video');
    const header = document.querySelector('#header-component');
    const scroller = findScroller(hero);
    const scrolled = () => Math.max(window.scrollY, scroller ? scroller.scrollTop : 0);

    const setTop = () => hero.style.setProperty('--stp-top', (header ? header.offsetHeight : 0) + 'px');
    setTop();
    if (header && 'ResizeObserver' in window) new ResizeObserver(setTop).observe(header);

    initBuyBar(hero, scroller);
    if (!video) return;

    const src = { desktop: hero.dataset.video, mobile: hero.dataset.videoMobile || hero.dataset.video };
    const mqPhone = matchMedia('(max-width: 899px)');
    const mqStill = matchMedia('(prefers-reduced-motion: reduce)');
    const lite = () => {
      const c = navigator.connection;
      return !!(c && (c.saveData || /(^|-)2g$/.test(c.effectiveType || '')));
    };

    /* 0 at the top of the page, 1 once the stomp has played out */
    function progress() {
      if (mqPhone.matches) return clamp(scrolled() / (media.offsetHeight * 0.8), 0, 1);
      const hold = hero.offsetHeight - stage.offsetHeight;
      return clamp(scrolled() / (hold + stage.offsetHeight * 0.3), 0, 1);
    }

    // gated seeks: never overlap, keep only the newest target
    let seekBusy = false, pendingTime = null;
    function seek(t) {
      if (!video.duration || !isFinite(video.duration)) return;
      if (seekBusy) { pendingTime = t; return; }
      seekBusy = true;
      video.currentTime = t;
    }
    video.addEventListener('seeked', () => {
      seekBusy = false;
      if (pendingTime !== null) { const t = pendingTime; pendingTime = null; seek(t); }
    });
    const timeFor = (p) => p * Math.max(0, video.duration - 0.05);

    let target = 0, shown = 0, rafId = null, last = 0, onScreen = true, active = false;
    function tick(now) {
      const dt = Math.min(100, now - (last || now));
      last = now;
      shown += (target - shown) * (1 - Math.pow(1 - 0.16, dt / 16.667));
      if (Math.abs(target - shown) < 0.0008) { shown = target; rafId = null; last = 0; }
      else rafId = requestAnimationFrame(tick);
      seek(timeFor(shown));
    }
    function onScroll() {
      target = progress();
      if (rafId === null && onScreen && active) rafId = requestAnimationFrame(tick);
    }
    new IntersectionObserver((entries) => {
      onScreen = entries[0].isIntersecting;
      if (onScreen) onScroll();
    }).observe(hero);

    const blobs = {};
    let mode = null, token = 0;
    async function apply() {
      const want = mqStill.matches || lite() ? null : mqPhone.matches ? 'mobile' : 'desktop';
      if (want === mode) return;
      mode = want;
      const t = ++token;
      hero.classList.remove('is-video-ready');
      active = false;
      seekBusy = false; pendingTime = null;
      if (!want || !src[want]) { video.removeAttribute('src'); video.load(); return; }
      let url = src[want];
      try {
        if (!blobs[want]) {
          const res = await fetch(src[want], { credentials: 'omit' });
          if (!res.ok) throw new Error('video ' + res.status);
          blobs[want] = URL.createObjectURL(await res.blob());
        }
        url = blobs[want];
      } catch (_) { /* fall back to streaming the file directly */ }
      if (t !== token) return;
      let ready = false;
      const onReady = () => {
        if (ready || t !== token) return;
        ready = true;
        active = true;
        shown = target = progress();
        seek(timeFor(shown));
        hero.classList.add('is-video-ready');
      };
      video.addEventListener('loadeddata', onReady, { once: true });
      video.addEventListener('canplay', onReady, { once: true });
      video.preload = 'auto';
      video.src = url;
      video.load();
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    if (scroller) scroller.addEventListener('scroll', onScroll, { passive: true });
    [mqPhone, mqStill].forEach((m) => m.addEventListener('change', apply));

    // Start once the still hero photo is showing, without waiting for every other script on the page
    let started = false;
    const go = () => {
      if (started) return;
      started = true;
      if ('requestIdleCallback' in window) requestIdleCallback(apply, { timeout: 800 });
      else setTimeout(apply, 200);
    };
    const still = media.querySelector('img');
    if (!still || still.complete) go();
    else {
      still.addEventListener('load', go, { once: true });
      still.addEventListener('error', go, { once: true });
      setTimeout(go, 3000);
    }
  });

  /* Phone buy bar: shows once the hero button has scrolled off, hides again over the footer.
     It is moved to <body> so no theme wrapper can clip or cover it, and it follows the
     scroll position directly rather than relying on visibility callbacks. */
  function initBuyBar(hero, scroller) {
    const bar = hero.querySelector('[data-stp-mbar]');
    const cta = hero.querySelector('.stp-hero__actions');
    if (!bar || !cta) return;
    document.querySelectorAll('body > [data-stp-mbar]').forEach((old) => old.remove());
    document.body.appendChild(bar);
    const link = bar.querySelector('a');
    const footer = document.querySelector('footer');
    let shown = null, queued = false;
    const update = () => {
      queued = false;
      const pastHero = cta.getBoundingClientRect().bottom < 0;
      const atFooter = footer ? footer.getBoundingClientRect().top < window.innerHeight : false;
      const show = pastHero && !atFooter;
      if (show === shown) return;
      shown = show;
      bar.classList.toggle('is-shown', show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
      if (link) link.tabIndex = show ? 0 : -1;
    };
    const queue = () => { if (!queued) { queued = true; requestAnimationFrame(update); } };
    window.addEventListener('scroll', queue, { passive: true });
    if (scroller) scroller.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', queue);
    update();
  }
})();
